import ApplicationController from './application'
import { DirectUpload } from '@rails/activestorage'
import Dropzone from 'dropzone'
import { getMetaValue, findElement, removeElement, insertAfter } from '../helpers/dropzone'

Dropzone.autoDiscover = false

export default class extends ApplicationController {
  static targets = ['wrapper', 'input', 'remove']

  static values = {
    existingFiles: Array,
    maxFiles: { type: Number, default: 10 },
    maxFileSize: { type: Number, default: 256 },
    acceptedFiles: { type: String, default: 'image/*,application/pdf' },
    previewsContainerClass: { type: String, default: 'dropzone-previews' },
    errorMessage: { type: String }
  }

  connect() {
    this.dropZone = new Dropzone(this.element, {
      url: this.url,
      headers: this.headers,
      maxFiles: this.maxFilesValue,
      maxFilesize: this.maxFileSizeValue,
      acceptedFiles: this.acceptedFilesValue,
      previewsContainer: `.${this.previewsContainerClassValue}`,
      previewTemplate: this.previewTemplate,
      autoQueue: false
    })

    this.bindEvents()
    this.existingFilesValue.forEach((element, index) => {
      let existingFile = { name: element['name'], size: element['size'], uniqueId: element['unique_id'] }
      let callback = null // Optional callback when it's done
      let crossOrigin = '*' // Added to the `img` tag for crossOrigin handling
      let resizeThumbnail = false // Tells Dropzone whether it should resize the image first
      this.dropZone.files.push(existingFile)
      this.dropZone.displayExistingFile(existingFile, element['url'], callback, crossOrigin, resizeThumbnail)
      this.#updateExistingFileIcon(existingFile, index)
    })

    // Find all Dropzone instances on the page
    this.dropzones = Dropzone.instances || []

    this.dropzones.forEach((dropzone) => {
      dropzone.on('processing', this.checkProcessingState.bind(this))
      dropzone.on('queuecomplete', this.checkProcessingState.bind(this))
    })

    this.checkProcessingState()
  }

  checkProcessingState() {
    // Check if any Dropzone instance is in a `processing` state
    const isProcessing = this.dropzones.some((dropzone) => dropzone.getUploadingFiles().length > 0)

    if (isProcessing) {
      this.disableSubmitButton()
    } else {
      this.enableSubmitButton()
    }
  }

  disableSubmitButton() {
    this.submitButton.disabled = true
    this.submitButton.classList.remove(
      'text-white',
      'bg-blue-700',
      'hover:bg-blue-800',
      'focus:bg-blue-900'
    )
    this.submitButton.classList.add(
      'cursor-not-allowed',
      'text-blue-500',
      'bg-blue-600',
      'hover:bg-blue-600',
      'focus:bg-blue-600'
    )
  }

  enableSubmitButton() {
    this.submitButton.disabled = false
    this.submitButton.classList.remove(
      'cursor-not-allowed',
      'text-blue-500',
      'bg-blue-600',
      'hover:bg-blue-600',
      'focus:bg-blue-600'
    )
    this.submitButton.classList.add(
      'text-white',
      'bg-blue-700',
      'hover:bg-blue-800',
      'focus:bg-blue-900'
    )
  }

  #updateExistingFileIcon(existingFile, index) {
    const previewTemplate = existingFile['previewTemplate']
    const elements = [
      { selector: '.progress', action: 'add', className: 'hidden' },
      { selector: '.progress-value', action: 'add', className: 'hidden' },
      { selector: '.complete', action: 'remove', className: 'hidden' }
    ]

    elements.forEach(({ selector, action, className }) => {
      previewTemplate.querySelector(selector).classList[action](className)
    })

    previewTemplate.querySelector('.dz-remove').setAttribute('data-index', index)
  }

  bindEvents() {
    let fileIndex = 0

    this.dropZone.on('addedfile', (file) => {
      findElement(file.previewTemplate, '.progress-name').innerHTML = file.name
      findElement(file.previewTemplate, '.progress-value').innerHTML = '0%'

      setTimeout(() => {
        fileIndex += 1
        file.errorMessage = this.errorMessageValue
        if (fileIndex > this.maxFilesValue) {
          file.status = 'error'
          this.dropZone.emit('complete', file)

          findElement(file.previewTemplate, '.progress-name').classList.add('text-red-600')
          findElement(file.previewTemplate, '.progress-name').classList.add('font-bold')
          findElement(file.previewTemplate, '.progress-value').classList.add('hidden')
        } else {
          file.accepted && new Uploader(this, file).uploadFile()
        }
      }, 500)
    })

    this.dropZone.on('removedfile', (file) => {
      fileIndex = file.previewTemplate.querySelector('.dz-remove').getAttribute('data-index')
      file.controller && removeElement(file.controller.hiddenInput)

      // Helps remove existing files
      if (!fileIndex) return
      const allUploads = this.wrapperTarget.querySelectorAll('[data-dropzone-target*="remove"]')
      const isExistingUpload = fileIndex < allUploads.length

      if (!isExistingUpload) return
      const isMultipleUpload = this.maxFilesValue > 1
      isMultipleUpload && removeElement(allUploads[fileIndex])
      !isMultipleUpload && (allUploads[fileIndex].value = '')
    })

    this.dropZone.on('canceled', (file) => {
      file.controller && file.controller.xhr.abort()
    })
  }

  get headers() { return { 'X-CSRF-Token': getMetaValue('csrf-token') } }

  get url() { return this.inputTarget.getAttribute('data-direct-upload-url') }

  get form() { return this.element.closest('form') }

  get submitButton() { return findElement(this.form, 'input[type=submit], button[type=submit]') }

  get previewTemplate() {
    return `<div class='flex w-full items-center rounded-lg p-5 gap-4 border border-gray-200 bg-white'>
      <div class='flex w-full gap-4 items-center'>
        <svg class='progress min-w-10 w-10 h-10' viewBox='0 0 120 120'>
          <circle cx='60' cy='60' r='54' fill='none' stroke='#e6e6e6' stroke-width='12' />
          <circle class='progress-bar' cx='60' cy='60' r='54' fill='none' stroke='#2B4FA2' stroke-width='12'
              stroke-dasharray='339.292' stroke-dashoffset='339.292' />
        </svg>
        <svg class='complete hidden min-w-10 w-10 h-10' viewBox='0 0 12 12' fill='none' xmlns='http://www.w3.org/2000/svg'>
          <g clip-path='url(#clip0_795_10572)'>
          <path fill-rule='evenodd' clip-rule='evenodd' d='M2.02253 2.02253C3.07742 0.967631 4.50816 0.375 6 0.375C7.49184 0.375 8.92256 0.967631 9.97748 2.02253C11.0324 3.07742 11.625 4.50816 11.625 6C11.625 7.49184 11.0324 8.92256 9.97748 9.97748C8.92256 11.0324 7.49184 11.625 6 11.625C4.50816 11.625 3.07742 11.0324 2.02253 9.97748C0.967631 8.92256 0.375 7.49184 0.375 6C0.375 4.50816 0.967631 3.07742 2.02253 2.02253ZM5.47242 8.6933L8.77566 4.56425L7.67756 3.68578L5.3558 6.58795L4.27067 5.50284L3.2763 6.4972L5.47242 8.6933Z' fill='#059669'/>
          </g>
          <defs>
          <clipPath id='clip0_795_10572'>
          <rect width='12' height='12' fill='white'/>
          </clipPath>
          </defs>
        </svg>
        <div>
          <p class='progress-name text-sm'></p>
          <p class='progress-value text-sm text-gray-500'></p>
          <div class='dz-error-message text-xs text-red-600'><span data-dz-errormessage></span></div>
          </div>
      </div>
      <a class='dz-remove' href='javascript:undefined;' data-dz-remove=''>
        <svg class='w-6' viewBox='0 0 16 16' fill='none' xmlns='http://www.w3.org/2000/svg'>
          <g clip-path='url(#clip0_1535_14540)'>
          <path fill-rule='evenodd' clip-rule='evenodd' d='M2.6967 2.6967C4.10323 1.29018 6.01087 0.5 8 0.5C9.98913 0.5 11.8967 1.29018 13.3033 2.6967C14.7098 4.10323 15.5 6.01087 15.5 8C15.5 9.98913 14.7098 11.8967 13.3033 13.3033C11.8967 14.7098 9.98913 15.5 8 15.5C6.01087 15.5 4.10323 14.7098 2.6967 13.3033C1.29018 11.8967 0.5 9.98913 0.5 8C0.5 6.01087 1.29018 4.10323 2.6967 2.6967ZM4.21155 10.4628L6.67427 8.00006L4.21155 5.53737L5.53737 4.21155L8.00006 6.67427L10.4628 4.21155L11.7886 5.53737L9.32594 8.00006L11.7886 10.4628L10.4628 11.7886L8.00006 9.32594L5.53737 11.7886L4.21155 10.4628Z' fill='#E11D48'/>
          </g>
          <defs>
          <clipPath id='clip0_1535_14540'>
          <rect width='24' height='24' fill='white'/>
          </clipPath>
          </defs>
        </svg>
      </a>
    </div>`
  }
}

class Uploader {
  constructor(source, file) {
    this.directUpload = new DirectUpload(file, source.url, this)
    this.source = source
    this.file = file
  }

  uploadFile() {
    this.file.controller = this
    this.hiddenInput = this.#createHiddenInput()
    this.directUpload.create((error, attributes) => {
      if (error) {
        removeElement(this.hiddenInput)
        this.#emitDropzoneError(error)
      } else {
        this.hiddenInput.value = attributes.signed_id
        this.#emitDropzoneSuccess()
      }
    })
    this.#clearValidation()
  }

  directUploadWillStoreFileWithXHR(xhr) {
    this.#bindProgressEvent(xhr)
    this.#emitDropzoneUploading()
  }

  #clearValidation() {
    const wrapper = this.source.wrapperTarget.closest('.main-wrapper')

    wrapper.querySelector('.dropzone-msg').classList.remove('border-red-300', 'bg-red-100', 'hover:bg-red-50')
    wrapper.querySelector('.text-wrapper').classList.remove('text-red-500')
    removeElement(wrapper.querySelector('.error-messages'))
  }

  #createHiddenInput() {
    const input = document.createElement('input')
    input.type = 'hidden'
    input.name = this.source.inputTarget.name
    insertAfter(input, this.source.inputTarget)
    return input
  }

  #bindProgressEvent(xhr) {
    this.xhr = xhr
    this.xhr.upload.addEventListener('progress', event => this.#uploadRequestDidProgress(event))
  }

  #uploadRequestDidProgress(event) {
    // Calculate upload progress percentage.
    let progress = (event.loaded / event.total) * 100

    // Ensure progress is within valid bounds (0 to 100).
    progress = Math.max(0, Math.min(progress, 100))

    // Find the progress bar element.
    const progressBar = findElement(this.file.previewTemplate, '.progress-bar')
    const r = progressBar.getAttribute('r') // Radius of the progress bar
    const circumference = Math.PI * (r * 2) // Calculate circumference of the circle

    // Calculate the stroke offset for the circular progress bar.
    const strokeDashOffset = ((100 - progress) / 100) * circumference
    progressBar.setAttribute('stroke-dashoffset', strokeDashOffset)

    // Update progress value display.
    const progressValue = progress < 100 ? `${Math.round(progress)}%` : 'Upload complete'
    findElement(this.file.previewTemplate, '.progress-value').innerHTML = progressValue

    // Update progress icon display.
    setTimeout(() => {
      if (progress >= 100) {
        findElement(this.file.previewTemplate, '.progress').classList.add('hidden')
        findElement(this.file.previewTemplate, '.complete').classList.remove('hidden')
      }
    }, 500)
  }

  #emitDropzoneUploading() {
    this.file.status = Dropzone.UPLOADING
    this.source.dropZone.emit('processing', this.file)
  }

  #emitDropzoneError(error) {
    this.file.status = Dropzone.ERROR
    this.source.dropZone.emit('error', this.file, this.file.errorMessage || error)
    this.source.dropZone.emit('complete', this.file)
  }

  #emitDropzoneSuccess() {
    this.file.status = Dropzone.SUCCESS
    this.source.dropZone.emit('success', this.file)
    this.source.dropZone.emit('complete', this.file)
  }
}
